import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateUserLanguage, getCurrentUser } from '../../../api/productService';
import { useAuthStore } from '../../../store/useAuthStore';
import './LanguageConfig.css';

interface Language {
  code: string;
  name: string;
}

const LanguageConfig: React.FC = () => {
  const [selected, setSelected] = useState('');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t, i18n } = useTranslation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/i18n/locales`);
        if (!response.ok) throw new Error('Failed to fetch languages');
        const data = await response.json();
        const onlyEnd = data.filter((item:any) => (item.code === 'en' || item.code === 'us'))
        setLanguages(onlyEnd);
      } catch (err) {
        setError(t('product.language.error'));
      }
    };

    const fetchUserLanguage = async () => {
      if (isAuthenticated) {
        try {
          const {user} = await getCurrentUser();
          setSelected(user.lang || i18n.language);
        } catch (err) {
          setSelected(i18n.language);
        }
      } else {
        setSelected(localStorage.getItem('user_language') || i18n.language);
      }
    };

    const initialize = async () => {
      setLoading(true);
      await Promise.all([fetchLanguages(), fetchUserLanguage()]);
      setLoading(false);
    };

    initialize();
  }, [isAuthenticated, i18n.language, t]);

  const handleLanguageSelect = async (code: string) => {
    setSelected(code);
    try {
      localStorage.setItem('user_language', code);
      await i18n.changeLanguage(code);
      if (isAuthenticated) {
        await updateUserLanguage(code);
      }
    } catch (err) {
      setError(t('product.language.error'));
    }
  };

  if (loading) {
    return <div className="language-config-container">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="language-config-container">{error}</div>;
  }

  return (
    <div className="language-config-page">
      <div className="language-config-list">
        {languages.map((lang) => {
          const getFlagSrc = (code: string) => {
            switch (code) {
              case 'es':
                return '/images/language/es_lang.png';
              case 'en':
              case 'us':
                return '/images/language/us_lang.png';
              case 'fr':
                return '/images/language/fr_lang.png';
              case 'pt':
                return '/images/language/pt_lang.png';
              case 'tr':
                return '/images/language/tr_lang.png';
              case 'ko':
                return '/images/language/ko_lang.png';
              case 'ja':
                return '/images/language/ja_lang.png';
              default:
                return '';
            }
          };

          const flagSrc = getFlagSrc(lang.code);
          return (
            <div
              key={lang.code}
              className={`language-config-item ${selected === lang.code ? 'selected' : ''}`}
              onClick={() => handleLanguageSelect(lang.code)}
            >
              <div className="language-config-item-left">
                {flagSrc ? (
                  <img src={flagSrc} alt={`${lang.name} flag`} className="language-config-flag" />
                ) : (
                  <div className="language-config-flag-placeholder" />
                )}
                <span className="language-config-name">{lang.name}</span>
              </div>
              {selected === lang.code && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="#556BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          );
        })}
      </div>
      <div className="delete-account-footer-text">©2025 AiCourses. All rights reserved.</div>
    </div>
  );
};

export default LanguageConfig;
